import React, { useEffect } from 'react'
import { Bitcoin, CreditCard, Wallet } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import usePaymentStore from '../store/usePaymentStore'
import useAudioManager from '../../../hooks/useAudioManager'

/**
 * PaymentMethodSelector Component
 * Компонент для выбора способа оплаты
 */
const PaymentMethodSelector = ({ onMethodClick }) => {
  const { t } = useTranslation()
  const { playSound } = useAudioManager({
    pick: 'pick'
  })
  const { 
    selectedMethod, 
    availableMethods, 
    isLoadingMethods,
    setSelectedMethod,
    loadAvailableMethods
  } = usePaymentStore()

  // Загружаем доступные способы оплаты при монтировании
  useEffect(() => {
    loadAvailableMethods()
  }, [loadAvailableMethods])

  // Маппинг иконок (изображения для Stars и SBP, Lucide для остальных)
  const getMethodIcon = (method) => {
    if (method.id === 'telegram_stars') {
      return <img src="/images/payment/star.png" alt="Telegram Stars" className="w-10 h-10" />
    }
    if (method.id === 'sbp' || method.id === 'sbp2') {
      return <img src="/images/payment/sbp.png" alt="СБП" className="w-10 h-10" />
    }
    if (method.id === 'card') {
      return <img src="/images/payment/card-rub.png" alt="Карты РФ" className="w-10 h-10" />
    }
    if (method.id === 'cardww1') {
      return <img src="/images/payment/card-ww.png" alt="VISA / MC World Wide" className="w-10 h-10" />
    }
    if (method.id === 'crypto') {
      return <img src="/images/payment/crypto.png" alt="Crypto" className="w-10 h-10" />
    }
    if (method.id === 'uzs') {
      return <img src="/images/payment/uzbekistan.png" alt="UZS" className="w-10 h-10" />
    }
    if (method.id === 'kzt1') {
      return <img src="/images/payment/kazakhstan.png" alt="KZT (Казахстан)" className="w-10 h-10" />
    }
    if (method.id === 'skins') {
      return <img src="/images/payment/steam.png" alt="Steam Skins" className="w-10 h-10" />
    }
    
    // Для остальных используем Lucide иконки
    const iconMap = {
      Bitcoin: Bitcoin,
      CreditCard: CreditCard,
      Wallet: Wallet
    }
    const Icon = iconMap[method.icon]
    return Icon ? <Icon size={24} className="text-white" /> : null
  }

  const getMethodTitle = (method) => {
    if (method.id === 'sbp') return `${t('payment.methods.sbp')} #1`
    if (method.id === 'sbp2') return `${t('payment.methods.sbp')} #2`
    if (method.id === 'cardww1') return 'VISA / MasterCard'

    return t(
      `payment.methods.${method.id
        .replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
        .replace('_', '')}`
    )
  }

  const getMethodDesc = (method) => {
    if (method.id === 'sbp') return `${t('payment.methods.sbpDesc')} #1`
    if (method.id === 'sbp2') return `${t('payment.methods.sbpDesc')} #2`
    if (method.id === 'cardww1') return t('payment.methods.cardDesc')

    return t(
      `payment.methods.${method.id
        .replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
        .replace('_', '')}Desc`
    )
  }

  // Если загружается
  if (isLoadingMethods) {
    return (
      <div className="mb-6">
        <h2 className="text-lg font-semibold mb-4">{t('payment.selectMethod')}</h2>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div key={i} className="bg-surface rounded-lg p-4 animate-pulse">
              <div className="w-10 h-10 bg-gray-600 rounded-lg mb-3"></div>
              <div className="h-4 bg-gray-600 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-gray-600 rounded w-full"></div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Если нет доступных способов
  if (availableMethods.length === 0) {
    return null
  }

  return (
    <div className="mb-6">
      <h2 className="text-lg font-semibold mb-4">{t('payment.selectMethod')}</h2>
      
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {availableMethods.map((method) => {
          const isSelected = selectedMethod === method.id
          
          return (
            <button
              key={method.id}
              onClick={() => {
                playSound('pick')
                setSelectedMethod(method.id)
                onMethodClick?.(method.id)
              }}
              className={`
                relative bg-surface rounded-lg p-4 text-left transition-all
                hover:scale-[1.02] active:scale-[0.98]
                ${isSelected 
                  ? 'ring-2 ring-accent-primary border-accent-primary/50' 
                  : 'border border-white border-opacity-10 hover:border-accent-primary/30'
                }
              `}
            >
              {/* Бейдж "Выбрано" */}
              {isSelected && (
                <div className="absolute -top-4 -right-2">
                  <span className="inline-block px-2 py-0.5 bg-accent-primary text-dark-primary text-xs font-bold rounded-full">
                    {t('payment.selected')}
                  </span>
                </div>
              )}

              {/* Иконка */}
              <div className={`
                w-10 h-10 rounded-lg flex items-center justify-center mb-3
              `}>
                {getMethodIcon(method)}
              </div>

              {/* Название */}
              <h3 className={`
                font-semibold mb-1
                ${isSelected ? 'text-accent-primary' : 'text-white'}
              `}>
                {getMethodTitle(method)}
              </h3>

              {/* Описание */}
              <p className="text-xs text-text-secondary line-clamp-2 hidden">
                {getMethodDesc(method)}
              </p>
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default PaymentMethodSelector
